import { createSelector } from "@reduxjs/toolkit";
import { useMemo } from "react";
import { useSelector } from "react-redux";

import type { DeviceKind, PresenceTab } from "../protocol/protocol";
import type { AgentState } from "../store/agentSlice";

type State = { agent: AgentState };

/** One of the user's tabs showing a thread, as the dots by the model selector show it. */
export interface TabDot {
  tabId: string;
  /** 0-5: the tab's caret colour. */
  colour: number;
  /** As the tab reported it; null until it does. */
  device: DeviceKind | null;
  /** The card on an application page, or a chat (/assistant, the General card). */
  source: "application" | "chat";
  isThis: boolean;
}

export interface TabDots {
  /** Dots show only when the thread is open in more than one tab. */
  show: boolean;
  /** This tab first, then by colour. H5 draws at most three, overlapping. */
  dots: TabDot[];
}

const NONE: TabDots = { show: false, dots: [] };

function toDot(tab: PresenceTab, thisTab: string): TabDot {
  return {
    tabId: tab.tab_id,
    colour: tab.colour,
    device: tab.device_kind ?? null,
    source:
      tab.view !== "assistant" && tab.context_ref?.startsWith("application:")
        ? "application"
        : "chat",
    isThis: tab.tab_id === thisTab,
  };
}

export const makeSelectTabDots = () =>
  createSelector(
    [
      (state: State) => state.agent.presence,
      (state: State) => state.agent.connection.tabId,
      (_state: State, threadId: number | null) => threadId,
    ],
    (tabs, thisTab, threadId): TabDots => {
      if (threadId == null) return NONE;
      const dots = tabs
        .filter((tab) => tab.thread_id === threadId)
        .map((tab) => toDot(tab, thisTab))
        .sort((a, b) => Number(b.isThis) - Number(a.isThis) || a.colour - b.colour);
      return dots.length > 1 ? { show: true, dots } : NONE;
    }
  );

export function useTabDots(threadId: number | null): TabDots {
  const select = useMemo(makeSelectTabDots, []);
  return useSelector((state: State) => select(state, threadId));
}
